import { Components, Theme } from '@mui/material/styles';

/**
 * ## `text.primary`, not `primary.contrastText` — including the SELECTED state
 *
 * `contrastText` is the colour legible ON the primary colour, i.e. on a
 * primary-FILLED surface. `primary.main` defaults to BLACK (`./palette.ts`), so MUI
 * computes `contrastText` as `#fff`, and neither state here is on a filled surface:
 *
 * - **rest** — a tab on the page background. White on white.
 * - **selected** — `backgroundColor: ${primary.main}15`, which is primary at ~8%
 *   ALPHA. That is a pale wash, not a fill: on the default black primary it resolves
 *   to roughly `#F2F2F2`, so white text on it is about 1.1:1. The `15` suffix is
 *   exactly what makes this different from `MuiPaginationItem` and `MuiChip`, whose
 *   `contrastText` is correct BECAUSE the same rule sets an opaque fill.
 *
 * ## Latent, not live — and the codebase already worked around it once
 *
 * There is no `<Tab>` or `<Tabs>` anywhere in `apps/app/src`; `.MuiTab-root` matched
 * 0 elements on every surface driven in the browser. So no screen is broken by this
 * today, and it should not be described as one.
 *
 * What DOES exist is `components/pages/certificates/tabsSx.ts`, whose entire
 * `& .MuiTab-root` block sets `color: text.primary` in both states — a call-site
 * patch around exactly this default, arrived at independently. (It is currently
 * imported by `components/pages/certificates/index.tsx` and never used, so it is
 * dead too; left alone here as out of scope.) A default that the one author who
 * needed it had to override is the wrong default (working-conventions §47).
 */
export const MuiTab: Components<Theme>['MuiTab'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      textTransform: 'capitalize',
      padding: '0px',
      minWidth: '75px',
      minHeight: '34px',
      flexGrow: 1,
      fontSize: '12px',
      borderBottom: `1px solid ${theme.palette.grey[400]})`,
      color: theme.palette.text.primary,
      '&.Mui-selected': {
        color: theme.palette.text.primary,
        fontWeight: '700',
        backgroundColor: `${theme.palette.primary.main}15 !important`,
        borderColor: `${theme.palette.primary.main} !important`,
      },
    }),
  },
};
